import {ObjectFactory} from "@commons/types";
import {merge} from "lodash";
import {Conversione} from "@commons/utils/Conversione";

function isNull(timb: string | null) {
    return (timb === null || timb === '');
}

export interface CoppiaTimbrature {
    entrata: string | null, // stringa che esprime hhmm,  es: "0800", "1730"
    utilizzaEntrata: boolean, // Utilizzata solo in frontend per editare lo stato della timbratura in <maskEffettive>
    generaEntrata: boolean,
    uscita: string | null, // stringa che esprime hhmm,  es: "0800", "1730"
    utilizzaUscita: boolean, // Utilizzata solo in frontend per editare lo stato della timbratura in <maskEffettive>
    generaUscita: boolean,
    entrataTerminale: string,
    uscitaTerminale: string,
}

export class CoppiaTimbrature {

    static factory: ObjectFactory<CoppiaTimbrature> = (json?: Partial<CoppiaTimbrature>) => {
        const that = merge<CoppiaTimbrature, Partial<CoppiaTimbrature> | undefined>({
            entrata: null,
            utilizzaEntrata: true,
            generaEntrata: false,
            uscita: null,
            utilizzaUscita: true,
            generaUscita: false,
            entrataTerminale: '',
            uscitaTerminale: '',
        }, json);
        that.entrata = that.entrata ? that.entrata : null;
        that.uscita = that.uscita ? that.uscita : null;
        return that;
    }

    static isNull = (coppia: CoppiaTimbrature | null) => {
        return !coppia || CoppiaTimbrature.getDurataIntervallo(coppia) === 0;
    }
    static isValid = function (coppia: CoppiaTimbrature) {
        return !isNull(coppia.entrata) && !isNull(coppia.uscita);
    };
    static isValidOne = function (coppia: CoppiaTimbrature) {
        return !isNull(coppia.entrata) || !isNull(coppia.uscita);
    };

    static getDurataIntervallo = (coppia: CoppiaTimbrature) => {
        const entrata = CoppiaTimbrature.hhmm2hhcc(coppia.entrata);
        const uscita = CoppiaTimbrature.hhmm2hhcc(coppia.uscita);
        if (entrata === 0 && uscita === 0) {
            return 0;
        } else {
            return uscita > entrata ? uscita - entrata : 24.0 - entrata + uscita;
        }
    }

    /**
     Restituisce un oggetto Number ricevendo in ingresso una stringa che rappresenta un ora espressa comme hhmm
     N.B. il numero decimale restituito rappresenta l'ora in ore e centesimi di ora
     *
     * @param value
     * @returns {Number}
     */
    static hhmm2hhcc = (value: string | null): number => Conversione.hhmm2hhcc(value);

    static formatHHMM = (value: string | null, defIfNUll: string = '00:00'): string => Conversione.hhmm2String(value, defIfNUll);

    /**
     *
     * @param value string hhmm
     * @param divisore int es. 10,15,30
     * @param difetto boolean true->uscita, false->entrata
     * @returns {string} hhmm
     */
    static arrotonda = function (value: string, divisore: number, difetto: boolean): string {
        if (value === null || value === '' || value === '0000') {
            return '0000';
        }
        let minutiArr: number;
        let ore: number = Number(value.substring(0, 2)), minuti = Number(value.substring(2));
        if (difetto) { // in uscita
            minutiArr = Math.floor(minuti / divisore) * divisore;
        } else // in entrata
        {
            minutiArr = Math.floor(minuti / divisore);
            if ((minuti % divisore) > 0) {
                ++minutiArr;
            }
            minutiArr *= divisore;
            if (minutiArr >= 60) {
                ++ore;
                minutiArr = 0;
            }
        }
        let strMinutiArr = '';
        if (minutiArr < 10) {
            strMinutiArr = strMinutiArr + '0' + minutiArr;
        } else {
            strMinutiArr = strMinutiArr + minutiArr;
        }
        let strOre = '';
        if (ore < 10) {
            strOre = strOre + '0' + ore;
        } else {
            strOre = strOre + ore;
        }
        return strOre + strMinutiArr;
    };


    static getEntrata = function (coppia: CoppiaTimbrature | null) {
        return coppia && coppia.entrata ? parseInt(coppia.entrata) : null;
    };
    static getUscita = function (coppia: CoppiaTimbrature | null) {
        return coppia && coppia.uscita ? parseInt(coppia.uscita) : null;
    };
    static setEntrata = function (coppia: CoppiaTimbrature, entrata: string | number | null) {
        if (!entrata && entrata !== '0' && entrata !== 0) { // zero va considerato valido!!!
            coppia.entrata = null;
        } else {
            coppia.entrata = entrata.toString().padStart(4, '0');
        }
    };
    static setUscita = function (coppia: CoppiaTimbrature, uscita: string | number | null) {
        if (!uscita && uscita !== '0' && uscita !== 0) { // zero va considerato valido!!!
            coppia.uscita = null;
        } else {
            coppia.uscita = uscita.toString().padStart(4, '0');
        }
    };
    static getDescrizioneCoppia = function (coppia: CoppiaTimbrature, dal: string = '', al: string = '&nbsp;-&nbsp;') {
        return '<span className={' + (coppia.generaEntrata ? '!text-[#A1A1A1]' : '') + '}>'
            + dal + CoppiaTimbrature.getDescEntrata(coppia) + '</span>'
            + '<span className={' + (coppia.generaUscita ? '!text-[#A1A1A1]' : '') + '}>'
            + al + CoppiaTimbrature.getDescUscita(coppia) + '</span>';
    };

    static getDescEntrata = function (coppia: CoppiaTimbrature, defIfNUll: string = '--:--') {
        return CoppiaTimbrature.formatHHMM(coppia.entrata, defIfNUll);
    };

    static getDescUscita = function (coppia: CoppiaTimbrature, defIfNUll: string = '--:--') {
        return CoppiaTimbrature.formatHHMM(coppia.uscita, defIfNUll);
    };

    static contains = function (that: CoppiaTimbrature, hhmm: string | null) {
        const entrata = CoppiaTimbrature.hhmm2hhcc(that.entrata);
        const uscita = CoppiaTimbrature.hhmm2hhcc(that.uscita);
        const hhcc = CoppiaTimbrature.hhmm2hhcc(hhmm);
        return (entrata <= hhcc && hhcc <= uscita);
    };

    static count = (source: CoppiaTimbrature[]) => {
        let strisciata: string = '';
        let count: number = 0;
        for (let indE = 0; indE < source.length; ++indE) {
            if (CoppiaTimbrature.getEntrata(source[indE]) !== null) {
                ++count;
            }
            if (CoppiaTimbrature.getUscita(source[indE]) !== null) {
                ++count;
            }
            strisciata += `${source[indE].entrata};${source[indE].uscita};`;
        }
        // console.log(strisciata);
        return count;
    }

    static strisciateAreEquals = (strisciataA: CoppiaTimbrature[], strisciataB: CoppiaTimbrature[]): boolean => {

        let lengthEffettive: number = 0;
        strisciataA.forEach(timb => {
            CoppiaTimbrature.getEntrata(timb) && lengthEffettive++;
            CoppiaTimbrature.getUscita(timb) && lengthEffettive++;
        });
        let lengthEffettiveOrig: number = 0;
        strisciataB.forEach(timb => {
            CoppiaTimbrature.getEntrata(timb) && lengthEffettiveOrig++;
            CoppiaTimbrature.getUscita(timb) && lengthEffettiveOrig++;
        });

        return (lengthEffettive === lengthEffettiveOrig)
            && strisciataA.every((coppia, i) => coppia.entrata === strisciataB[i].entrata && coppia.uscita === strisciataB[i].uscita);
    }

}